/*
 * Copyright (c) 2021.
 * This file is part of the Compo B2B application.
 * (c) Compo soft - Ganturin Vladimir <gun2rin@gmail.com>
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import {ChangeDetectionStrategy, Component, DOCUMENT, Inject, inject, OnInit} from '@angular/core';
import {NavigationEnd, RouterLink, RouterLinkActive, RouterOutlet} from '@angular/router';

import {NbButtonModule, NbLayoutModule, NbMenuItem, NbMenuModule, NbSidebarModule, NbSidebarService} from '@nebular/theme';
import {AuthService} from 'compob2b-auth';
import {
  BaseComponent,
  Contract,
  Customer,
  FilterCountComponent,
  ListFilterService,
  LOCAL_STORAGE,
  ModalMenuComponent,
  Settings,
  StoreService
} from 'compob2b-libs';
import {Observable} from 'rxjs';

import {filter, map, shareReplay, takeUntil, tap} from 'rxjs/operators';
import {IS_SHOWCASE} from '../tokens/is-showcase.token';
import {PopupLoginComponent} from '../../../auth/popup-login/login.component';
import {BreadcrumbService} from '../../breadcrumbs/breadcrumbs.service';
import {PopupCompanyComponent} from '../modal-company/modal-company.component';
import {ModalExcelExportComponent} from '../modal-excel-export/modal-excel-export.component';
import {ModalLocationComponent} from '../modal-location/modal-location.component';

import {ModalSuggestIdeaComponent} from '../modal-suggest-idea/modal-suggest-idea.component';
import {AsyncPipe, NgClass} from '@angular/common';
import {ModalCatalogComponent} from '../../catalog/modal-catalog/modal-catalog.component';
import {TuiIcon} from '@taiga-ui/core';
import {SearchAutocompleteComponent} from '../../search-autocomplete/search-autocomplete.component';
import {MatMenu, MatMenuItem, MatMenuTrigger} from '@angular/material/menu';
import {BasketHeaderComponent} from '../../cart/basket-header/basket-header.component';
import {LocaleSwitcherComponent} from '../locale/locale-switcher.component';
import {BreadcrumbComponent} from '../../breadcrumbs/breadcrumb-component/breadcrumb.component';
import {BreadcrumbItemDirective} from '../../breadcrumbs/breadcrumb-item.directive';
import {FilterComponent} from '../../filter-component/filter.component';
import {PageLinkComponent} from '../page-link/page-link.component';
import {rewriteMenuLinks} from '../routing/legacy-url-map';
import {entityPathByKey} from '../routing/url-builder';

@Component({
  templateUrl: './layout.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./layout.component.scss'],
  imports: [NbLayoutModule, NgClass, RouterLink, ModalCatalogComponent, NbButtonModule, TuiIcon, FilterCountComponent, SearchAutocompleteComponent, MatMenuTrigger, MatMenu, MatMenuItem, BasketHeaderComponent, RouterLinkActive, LocaleSwitcherComponent, NbSidebarModule, NbMenuModule, ModalMenuComponent, BreadcrumbComponent, BreadcrumbItemDirective, FilterComponent, RouterOutlet, PageLinkComponent, AsyncPipe]
})

export class LayoutComponent extends BaseComponent implements OnInit {
  contract: Contract;
  theme: string;
  isFullMenu = false;
  isShowSearch = false;
  isHomePage = true;
  name = 'Angular';
  checked = true;
  settings: Settings;
  menuItems$: Observable<NbMenuItem[]>;
  sideBarState: string;
  iconMenu = true;
  itemsProfile;
  customer: Customer;
  socialLinks: any;
  readonly isShowcase = inject(IS_SHOWCASE);

  readonly customerGroup$ = this.storeService.getCustomerGroup();
  private readonly customer$ = this.storeService.getCustomerAsync();
  private readonly localStorage = inject(LOCAL_STORAGE);

  constructor(private readonly sidebarService: NbSidebarService,
              private readonly storeService: StoreService,
              protected readonly authService: AuthService,
              readonly productFilter: ListFilterService,
              protected readonly breadcrumbService: BreadcrumbService,
              @Inject(DOCUMENT) private document: Document,
  ) {
    super();
    this.settings = this.storeService.getSettings();
  }

  ngOnInit() {
    if (this.isShowcase || this.authService.isAnonymous()) {
      this.document.body.classList.add('anon-role');
    }

    // Меню рендерится напрямую из `item.link` (шаблон, строки 69/89/99), а бэк
    // отдаёт `menu.uri` в legacy-форме. Без переписи сайдбар остался бы на
    // legacy-ссылках и каждый клик шёл бы через 301.
    //
    // Правим ЗДЕСЬ, а не в `StoreService.getMenu()`: тот — общий метод
    // `compob2b-libs`, его зовёт и админка, у которой в `admin_menu.uri` лежат
    // `/cabinet/pim/brand` и т.п. — общее правило `/cabinet/` → `/account/`
    // убило бы админский сайдбар целиком (ТЗ §11.1).
    //
    // `shareReplay` — потому что шаблон подписывается ДВАЖДЫ (`| async` в сайдбаре и
    // в мобильном меню). Без него перепись прогонялась бы на каждую подписку и
    // отдавала бы двум местам РАЗНЫЕ массивы (функция не мутирует, а копирует).
    // `refCount` — чтобы подписка на источник умирала вместе с лейаутом.
    this.menuItems$ = this.storeService.getMenu().pipe(
      map(items => rewriteMenuLinks(items)),
      shareReplay({bufferSize: 1, refCount: true}),
    );
    this.setSocialLinks();
    if (!this.isShowcase) {
      this.storeService.initCustomerGroup();
    }
    this.isHome();
    // Начальное состояние панели из localStorage (юзер мог открыть её ранее) —
    // чтобы filter.component при старте знал, грузить ли поля фильтра.
    this.productFilter.isPanelOpen.set(this.isShowFilter);
    this.closeFilterIfNotCatalog();

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd), takeUntil(this.unsubscribe))
      .subscribe(() => {
        this.isHome();
        this.closeFilterIfNotCatalog();
      });

    this.itemsProfile = this.getItemsProfile();

    this.sideBarState = this.storeService.getSidebarState();
    if (this.sideBarState === 'expanded') {
      this.isFullMenu = true;
    }

    this.customer$.pipe(
      tap((customer: Customer) => this.handleCustomerResponse(customer)),
      takeUntil(this.unsubscribe)
    ).subscribe();

  }

  setSocialLinks() {
    if (this.settings?.socialMediaSettings) {
      this.socialLinks = {...this.settings.socialMediaSettings};
    } else {
      this.socialLinks = {...this.settings};
    }
  }

  private handleCustomerResponse(customer: Customer) {
    this.customer = customer;
    if (customer?.contracts) {
      const index = customer.contracts.findIndex(contract => contract.id === customer.contractId);
      if (index !== -1) {
        this.contract = customer.contracts[index];
      }
    }

    this.cdr.detectChanges();
  }

  toggleSidebar(open = null) {
    if (open !== null) {
      if (open == this.isFullMenu) {
        return;
      }
    }
    this.iconMenu = !this.iconMenu;
    this.storeService.toggleSidebarState();
    this.sideBarState = this.storeService.getSidebarState();
    this.isFullMenu = !this.isFullMenu;
    this.sidebarService.toggle(true, 'menu-sidebar');
  }

  toggleFilter() {
    if (this.isShowFilter) {
      this.localStorage.removeItem('isShowFilter');
    } else {
      this.localStorage.setItem('isShowFilter', '1');
    }
    this.productFilter.isPanelOpen.set(this.isShowFilter);
  }

  get isShowFilter(): boolean {
    return !!this.localStorage.getItem('isShowFilter');
  }

  // Сбрасывает state фильтра при уходе с каталога, иначе на мобиле
  // fullscreen-overlay фильтра перекрывает контент на промо-страницах,
  // новостях и текст-страницах после возврата с каталога.
  private closeFilterIfNotCatalog(): void {
    const url = this.router.url.split('?')[0];
    const isCatalogUrl = url.startsWith('/catalog');
    if (!isCatalogUrl && this.isShowFilter) {
      this.localStorage.removeItem('isShowFilter');
      this.productFilter.isPanelOpen.set(false);
      this.cdr.detectChanges();
    }
  }

  openSearch() {
    this.isShowSearch = !this.isShowSearch;
  }

  open() {
    this.windowService.open(PopupLoginComponent, {
      title: 'Вход для пользователя',
      windowClass: 'small',
    });
  }

  openLocationWindow() {
    this.windowService.open(ModalLocationComponent, {windowClass: 'ds-modal-city'});
  }

  openCompanyWindow() {
    this.windowService.open(PopupCompanyComponent, {windowClass: 'ds-modal-company'});
  }

  openSuggestIdeaModal($event) {
    $event.preventDefault();
    this.windowService.open(ModalSuggestIdeaComponent, {windowClass: 'popup'});
  }

  toggle(checked: boolean) {
    this.checked = checked;
  }

  private isHome(): void {
    // Главная переехала в корень. exact-матч корня в Angular Router ведёт себя
    // иначе, чем матч непустого пути — проверено в браузере (ТЗ §5.5).
    this.isHomePage = this.router.isActive('/', {
      paths: 'exact',
      fragment: 'ignored',
      queryParams: 'ignored',
      matrixParams: 'ignored',
    });
  }

  openExcelExportWindow() {
    this.windowService.open(ModalExcelExportComponent, {windowClass: 'popup-export-excel', context: this.customer});
  }

  // SSR-safe: defaultView отсутствует на сервере, клик возможен только в браузере.
  scrollToTop() {
    this.document.defaultView?.scrollTo({top: 0, behavior: 'smooth'});
  }

  getItemsProfile() {
    const isManager = this.authService.isAdmin() || this.authService.isManager();

    if (isManager) {
      return [
        {title: this.translations.profile, link: '/account/profile'},
        {title: this.translations.myCompany, link: '/account/company'},
        {title: this.translations.exit}];
    }
    return [
      {title: this.translations.profile, link: '/account/profile'},
      {title: this.translations.myCompany, link: '/account/company'},
      {title: this.translations.apiTokens, link: '/account/profile/tokens'},
      {title: this.translations.notificationCustomer, link: '/account/notifications'},
      // Единственный легальный вызов `entityPathByKey` (ТЗ §6.3): id оферты зашит
      // константой, объекта страницы в памяти нет. Резолвер §6.4 дозаполнит slug.
      {title: this.translations.offerContract, link: entityPathByKey('pages', 5)},
      {
        title: this.translations.exit,
        click: () => {
          this.authService.logout();
        },
      }];
  }
}
